import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Eye, Search, DollarSign, FileText, CreditCard } from 'lucide-react';
import { useDebounce, useUrlTable } from '@/hooks';
import { useProjects, useUpdateBillingStatus } from '@/hooks/api/useProjects';
import Routes from '@/router/routes';
import { BillingStatus, type ProjectFilters } from '@/api/projects';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead, SortOrder } from '@/components/ui/sortable-table-head';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDate } from '@/lib/date';

interface TableState {
  page: number;
  limit: number;
  search: string;
  includeDeleted: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export default function SuperAdminProjects() {
  const navigate = useNavigate();
  const { state, setPage, setSearch, setFilter, setSort, resetFilters } = useUrlTable<TableState>({
    page: 1,
    limit: 25,
    search: '',
    includeDeleted: 'false',
    sortBy: '',
    sortOrder: 'desc',
  });

  const handleRowClick = (projectId: string) => {
    navigate(`${Routes.super_admin_dashboard_projects.path}/${projectId}`);
  };

  const debouncedSearch = useDebounce(state.search, 300);

  // Build API filters
  const filters = {
    page: state.page,
    limit: state.limit,
    search: debouncedSearch || undefined,
    includeDeleted: state.includeDeleted === 'true',
    sortBy: (state.sortBy || undefined) as ProjectFilters['sortBy'],
    sortOrder: state.sortOrder,
  };

  const handleSort = (sortBy: string, sortOrder: SortOrder) => {
    setSort(sortBy, sortOrder);
  };

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  const { data, isLoading, isError } = useProjects(filters);
  const updateBillingStatus = useUpdateBillingStatus();

  const handleBillingStatusChange = (projectId: string, status: BillingStatus) => {
    const statusLabel = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    setConfirmDialog({
      open: true,
      title: 'Update Billing Status',
      description: `Are you sure you want to set the billing status to "${statusLabel}"?`,
      onConfirm: () => {
        updateBillingStatus.mutate(
          { id: projectId, payload: { billingStatus: status } },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
    });
  };

  const getBillingBadgeVariant = (
    status?: BillingStatus,
  ): 'default' | 'warning' | 'success' | 'info' => {
    switch (status) {
      case BillingStatus.QUOTED:
        return 'warning';
      case BillingStatus.INVOICED:
        return 'info';
      case BillingStatus.PAID:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Projects</h1>
        <p className="text-muted-foreground mt-1">
          View and manage all projects across organizations
        </p>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search projects..."
                value={state.search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="includeDeleted"
                checked={state.includeDeleted === 'true'}
                onCheckedChange={(checked) =>
                  setFilter('includeDeleted', checked ? 'true' : 'false')
                }
              />
              <label
                htmlFor="includeDeleted"
                className="text-sm text-foreground cursor-pointer select-none"
              >
                Include deleted
              </label>
            </div>
            {(state.search || state.includeDeleted === 'true') && (
              <Button variant="outline" size="sm" onClick={resetFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        {isLoading ? (
          <div className="p-4">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 flex-1" />
                </div>
              ))}
            </div>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-red-600">Failed to load projects. Please try again.</p>
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No projects found.</p>
            {(state.search || state.includeDeleted === 'true') && (
              <Button variant="link" onClick={resetFilters} className="mt-2">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    sortKey="title"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Title
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="status"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="billingStatus"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Billing
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="organizationName"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Organization
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="workspaceName"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Workspace
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="respondentsCount"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Respondents
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="conversationsTotal"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Conversations
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="createdAt"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Created
                  </SortableTableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((project) => (
                  <TableRow
                    key={project._id}
                    className="cursor-pointer hover:bg-muted"
                    onClick={() => handleRowClick(project._id)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {project.title}
                        {project.deletedAt && (
                          <Badge variant="deleted" className="text-xs">
                            Deleted
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(project.status)}>
                        {project.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getBillingBadgeVariant(project.billingStatus)}>
                        {project.billingStatus || 'None'}
                      </Badge>
                    </TableCell>
                    <TableCell>{project.organizationName}</TableCell>
                    <TableCell>{project.workspaceName}</TableCell>
                    <TableCell>
                      {project.respondentsCount} / {project.respondentsMax}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span>{project.conversationsCompleted}</span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-muted-foreground">{project.conversationsTotal}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(project.createdAt)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(project._id);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()}>
                              <DollarSign className="h-4 w-4 mr-2" />
                              Billing Status
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBillingStatusChange(project._id, BillingStatus.QUOTED);
                                }}
                                disabled={project.billingStatus === BillingStatus.QUOTED}
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Quoted
                                {project.billingStatus === BillingStatus.QUOTED && ' ✓'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBillingStatusChange(project._id, BillingStatus.INVOICED);
                                }}
                                disabled={project.billingStatus === BillingStatus.INVOICED}
                              >
                                <DollarSign className="h-4 w-4 mr-2" />
                                Invoiced
                                {project.billingStatus === BillingStatus.INVOICED && ' ✓'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBillingStatusChange(project._id, BillingStatus.PAID);
                                }}
                                disabled={project.billingStatus === BillingStatus.PAID}
                              >
                                <CreditCard className="h-4 w-4 mr-2" />
                                Paid
                                {project.billingStatus === BillingStatus.PAID && ' ✓'}
                              </DropdownMenuItem>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <div className="text-sm text-muted-foreground">
                {data.totalPages > 1 ? (
                  <>
                    Showing {(data.page - 1) * data.limit + 1} to{' '}
                    {Math.min(data.page * data.limit, data.total)} of {data.total} projects
                  </>
                ) : (
                  <>
                    Showing {data.total} {data.total === 1 ? 'project' : 'projects'}
                  </>
                )}
              </div>
              {data.totalPages > 1 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(data.page - 1)}
                    disabled={data.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(data.page + 1)}
                    disabled={data.page === data.totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        loading={updateBillingStatus.isPending}
      />
    </div>
  );
}
